import type { SiteTimeline } from '@/domain/fishGroups';

import type { LastClosure, LineageBlock, WeekAxis } from '../types';

import { endedKindOf } from './groupTrack';
import { buildLineageBlocks } from './lineageBlocks';
import { buildWeekAxis, isOnAxis, positionOf } from './weekAxis';

/** `combineChains` draws groups that continue each other in the same pen on one row. */
export function lineageBlocksFor(
    timeline: SiteTimeline,
    combineChains = false
): {
    axis: WeekAxis;
    blocks: LineageBlock[];
    todayPct: number | null;
    lastClosure: LastClosure | null;
} {
    const now = timeline.generatedAt;
    const axis = buildWeekAxis(timeline.groups, now);
    return {
        axis,
        blocks: buildLineageBlocks(timeline, axis, now, combineChains),
        todayPct: isOnAxis(axis, now) ? positionOf(axis, now) : null,
        lastClosure: lastClosureOf(timeline),
    };
}

/**
 * A cycle's `lastClosedAt` is its latest group close for any reason; only the timeline says
 * how each group ended, so only it can tell a last harvest from a last closure.
 */
export function lastClosureOf(timeline: SiteTimeline): LastClosure | null {
    const ended = timeline.groups.filter((group) => !group.isLive && group.lifecycleEndedAt);
    if (ended.length === 0) return null;
    const at = Math.max(...ended.map((group) => group.lifecycleEndedAt?.getTime() ?? 0));
    const last = ended.filter((group) => group.lifecycleEndedAt?.getTime() === at);
    return { at: new Date(at), harvest: last.every((group) => endedKindOf(group) === 'harvest') };
}
